import React, { useEffect, useContext, useState, useCallback } from "react";
import { RootStoreContext } from "src/stores/RootStore";
import { UI, ScreenSize } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { format, isSameDay } from "date-fns";
import {
    GetCoownerFilesByRealEstateId_file_categories as file_category,
    GetCoownerFilesByRealEstateId_realestates_files as file_item,
    GetCoownerFilesByRealEstateId_realestates as file_realestates
} from "src/api/generated/GetCoownerFilesByRealEstateId";
import { NetworkConfig } from "src/network/NetworkConfig";
import { ColorStyle } from "src/utils/Colors";
import { observer } from "mobx-react";
import { ScreenWidth } from "src/stores/UIStore";
import styles from "./DocumentsList.module.css";
import { v4 as uuidv4 } from "uuid";
import { DocumentFilter } from "src/screens/realestates/realestate/documents/filter/DocumentsFilter";
import { Role } from "src/network/User";
import { getAssemblyDocumentDisplayName, IAssemblyDocumentTitle } from "./DocumentsList.utils";

interface FileCategory {
    key: number;
    label: string;
    total: number;
}

enum FILE_CATEGORY {
    ASSEMBLY_DOCUMENTS = 4
}

interface DocumentsListProps {
    realestateFileItems: file_realestates[];
    fileCategories: file_category[];
    assemblyDocumentTitles: IAssemblyDocumentTitle[];
    onDocumentOpened?: () => void;
}

interface SearchAndFilterArguments {
    searchQuery?: string;
    fileCategoryKey?: number;
    itemsToSearch?: file_item[];
}

export const DocumentsListBase = (props: DocumentsListProps) => {
    const { t } = useTranslation();
    const { navStore, uiStore, documentStore, authStore } = useContext(RootStoreContext);

    const [initialFileItems, setInitialFileItems] = useState<file_item[]>();
    const [displayedFileCategories, setDisplayedFileCategories] = useState<FileCategory[]>([]);
    const [activeFileCategory, setActiveFileCategory] = useState<FileCategory>();
    const [currentSearchQuery, setCurrentSearchQuery] = useState<string>("");
    const [hasInitialized, setHasInitialized] = useState<boolean>(false);

    const getUpdatedDisplayedDocuments = useCallback(
        (searchAndFilterArguments: SearchAndFilterArguments, newActiveFileCategory: FileCategory): file_item[] => {
            const { searchQuery, itemsToSearch } = searchAndFilterArguments;
            const updatedDisplayedDocuments: file_item[] = [];
            const query = searchQuery?.toLocaleLowerCase();

            const selectedYears = documentStore.selectedYears;
            const yearFilterActive = selectedYears.length > 0;

            itemsToSearch?.forEach((fileItem) => {
                const { filedate, name, category: fileCategory } = fileItem;
                const fileName = name?.toLowerCase();
                const fileDate = format(new Date(filedate), "dd.MM.yyyy").toLocaleLowerCase();
                const fileYear = new Date(filedate).getFullYear().toString();

                const isFileNameOrDateIncludedInQuery =
                    query !== null && query !== undefined
                        ? fileName?.includes(query) || fileDate.includes(query)
                        : false;

                if (yearFilterActive && !selectedYears.includes(fileYear)) {
                    return;
                }

                if (newActiveFileCategory?.key === fileCategory && isFileNameOrDateIncludedInQuery) {
                    updatedDisplayedDocuments.push(fileItem);
                } else if (!newActiveFileCategory?.key && uiStore.isMobile && isFileNameOrDateIncludedInQuery) {
                    updatedDisplayedDocuments.push(fileItem);
                } else if (
                    !newActiveFileCategory?.key &&
                    isFileNameOrDateIncludedInQuery &&
                    fileCategory === newActiveFileCategory?.key
                ) {
                    updatedDisplayedDocuments.push(fileItem);
                }
            });

            return updatedDisplayedDocuments;
        },
        [uiStore.isMobile, documentStore.selectedYears]
    );

    const getUpdatedDisplayedDocumentsAllCategories = useCallback(
        (searchAndFilterArguments: SearchAndFilterArguments): file_item[] => {
            const { searchQuery, itemsToSearch } = searchAndFilterArguments;
            const foundDocumentsAllCategories: file_item[] = [];
            const query = searchQuery?.toLocaleLowerCase();

            const selectedYears = documentStore.selectedYears;
            const yearFilterActive = selectedYears.length > 0;

            itemsToSearch?.forEach((fileItem) => {
                const { filedate, name } = fileItem;
                const fileName = name?.toLowerCase();
                const fileDate = format(new Date(filedate), "dd.MM.yyyy").toLocaleLowerCase();
                const fileYear = new Date(filedate).getFullYear().toString();

                if (yearFilterActive && !selectedYears.includes(fileYear)) {
                    return;
                }

                const isFileNameOrDateIncludedInQuery = query
                    ? fileName?.includes(query) || fileDate.includes(query)
                    : false;

                if (query && isFileNameOrDateIncludedInQuery) {
                    foundDocumentsAllCategories.push(fileItem);
                } else if (!query) {
                    foundDocumentsAllCategories.push(fileItem);
                }
            });

            return foundDocumentsAllCategories;
        },
        [documentStore.selectedYears]
    );

    const getUpdatedDisplayedFileCategories = useCallback(
        (foundDocumentsAllCategories: file_item[]): FileCategory[] => {
            const fileCategories: FileCategory[] = [];
            const filteredFileCategories: FileCategory[] = [];

            props.fileCategories.forEach((fileCategory) => {
                if (navStore.isCondominium || fileCategory.key !== FILE_CATEGORY.ASSEMBLY_DOCUMENTS) {
                    fileCategories.push({
                        label: fileCategory.label || "-",
                        key: fileCategory.key ?? 0,
                        total: fileCategory.count || 0
                    });
                }
            });

            fileCategories.forEach((fileCategory) => {
                foundDocumentsAllCategories.forEach((fileItem) => {
                    const alreadyExistingCategory = filteredFileCategories.find(
                        (filteredFileCategory) =>
                            filteredFileCategory.label === fileItem.categorylabel &&
                            filteredFileCategory.key === fileItem.category
                    );

                    if (!alreadyExistingCategory && fileCategory.label === fileItem.categorylabel) {
                        filteredFileCategories.push({
                            label: fileCategory.label,
                            key: fileCategory.key,
                            total: fileCategory.total
                        });
                    }
                });
            });

            return filteredFileCategories;
        },
        [navStore.isCondominium, props.fileCategories]
    );

    const getActiveFileCategory = useCallback(
        (fileCategoryKey: number | undefined, filteredFileCategories: FileCategory[]): FileCategory => {
            let newActiveFileCategory = undefined;

            if (fileCategoryKey) {
                const fileCategory = filteredFileCategories.find(
                    (fileCategory) => fileCategory.key === fileCategoryKey
                );
                if (fileCategory) {
                    newActiveFileCategory = fileCategory;
                } else {
                    newActiveFileCategory = filteredFileCategories[0];
                }
            } else {
                newActiveFileCategory = filteredFileCategories[0];
            }

            return newActiveFileCategory!;
        },
        []
    );

    const searchAndFilterDocuments = useCallback(
        (searchAndFilterArguments: SearchAndFilterArguments) => {
            const { fileCategoryKey } = searchAndFilterArguments;

            const updatedDisplayedDocumentsAllCategories =
                getUpdatedDisplayedDocumentsAllCategories(searchAndFilterArguments);
            const updatedFileCategories = getUpdatedDisplayedFileCategories(updatedDisplayedDocumentsAllCategories);
            const newActiveFileCategory = getActiveFileCategory(fileCategoryKey, updatedFileCategories);
            const updatedDisplayedDocuments = getUpdatedDisplayedDocuments(
                searchAndFilterArguments,
                newActiveFileCategory
            );

            documentStore.setDisplayedDocuments([...updatedDisplayedDocuments]);

            setDisplayedFileCategories(updatedFileCategories);

            if (uiStore.isMobile && !hasInitialized) {
                setActiveFileCategory({ key: -1, label: "", total: 0 });
            } else {
                if (documentStore.focusedFileCategoryKey) {
                    const focusedCategory = updatedFileCategories.find(
                        (category) => category.key === documentStore.focusedFileCategoryKey
                    );
                    setActiveFileCategory(focusedCategory || newActiveFileCategory);
                } else {
                    setActiveFileCategory(newActiveFileCategory);
                }
            }
        },
        [
            getUpdatedDisplayedFileCategories,
            getActiveFileCategory,
            getUpdatedDisplayedDocuments,
            getUpdatedDisplayedDocumentsAllCategories,
            hasInitialized,
            uiStore.isMobile,
            documentStore
        ]
    );

    // Run once on mount to initialize everything (unchanged)
    useEffect(() => {
        if (!hasInitialized) {
            const allFilesUniquePerCategory = Object.values(
                props.realestateFileItems
                    .flatMap((realEstate) => realEstate.files)
                    .reduce<Record<string, Record<string, file_item>>>((accumulator, file) => {
                        const category = file.category || "nullCategory";
                        if (!accumulator[category]) accumulator[category] = {};
                        accumulator[category][file.fileid] = file;
                        return accumulator;
                    }, {})
            )
                .flatMap((categoryFiles) => Object.values(categoryFiles))
                .sort((a, b) => {
                    const fileDateA = a.filedate ? new Date(a.filedate) : new Date();
                    const fileDateB = b.filedate ? new Date(b.filedate) : new Date();

                    if (isSameDay(fileDateA, fileDateB)) {
                        const fileNameA = a.name ?? "";
                        const fileNameB = b.name ?? "";
                        return fileNameA.localeCompare(fileNameB);
                    }

                    return fileDateB.getTime() - fileDateA.getTime();
                });

            setInitialFileItems(allFilesUniquePerCategory);
            searchAndFilterDocuments({
                searchQuery: "",
                itemsToSearch: allFilesUniquePerCategory
            });
            setHasInitialized(true);
        }
    }, [hasInitialized, searchAndFilterDocuments, props.realestateFileItems]);

    // Whenever selectedYears changes, re‐run the same search & category filter.
    useEffect(() => {
        if (hasInitialized && initialFileItems) {
            searchAndFilterDocuments({
                searchQuery: currentSearchQuery,
                fileCategoryKey: activeFileCategory?.key,
                itemsToSearch: initialFileItems
            });
        }
    }, [
        documentStore.selectedYears,
        hasInitialized,
        initialFileItems,
        currentSearchQuery,
        activeFileCategory?.key,
        searchAndFilterDocuments
    ]);

    const onChangeQueryInput = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const currentSearchQuery = e.target.value;
            setCurrentSearchQuery(currentSearchQuery);
            searchAndFilterDocuments({
                searchQuery: currentSearchQuery,
                fileCategoryKey: activeFileCategory?.key,
                itemsToSearch: initialFileItems
            });
        },
        [searchAndFilterDocuments, activeFileCategory?.key, initialFileItems]
    );

    const onTabNavigationClick = useCallback(
        (clickedFileCategoryKey: number, fileCategoryLabel: string, totalUnrealFiles: number) => async () => {
            searchAndFilterDocuments({
                searchQuery: currentSearchQuery,
                fileCategoryKey: clickedFileCategoryKey,
                itemsToSearch: initialFileItems
            });
            documentStore.setFocusedFileCategoryKey(clickedFileCategoryKey);
            setActiveFileCategory({ key: clickedFileCategoryKey, label: fileCategoryLabel, total: totalUnrealFiles });
        },
        [currentSearchQuery, searchAndFilterDocuments, initialFileItems, documentStore]
    );

    const tabNavigationItem = useCallback(
        (fileCategoryKey: number, fileCategoryLabel: string, totalUnrealFiles: number) => {
            return (
                <UI.Nav.Item key={uuidv4()} className={styles.TabNavigationNavItem}>
                    <UI.Nav.Link
                        eventKey={fileCategoryKey}
                        className={styles.TabNavigationNavLink}
                        onClick={onTabNavigationClick(fileCategoryKey, fileCategoryLabel, totalUnrealFiles)}
                    >
                        <div className="d-flex align-items-center">
                            <UI.Icon
                                icon={UI.SVGIcon.Folders}
                                color={ColorStyle("primary")}
                                size="medium"
                                className={styles.TabNavigationNavLinkIcon}
                            />
                            <div className={styles.TabNavigationNavLinkText}>{fileCategoryLabel}</div>
                            {authStore.user?.role === Role.COOWNER && totalUnrealFiles > 0 ? (
                                <div className={styles.TotalUnreadFiles}>{totalUnrealFiles}</div>
                            ) : undefined}

                            <UI.Icon
                                icon={uiStore.isMobile ? UI.SVGIcon.ChevronDown : UI.SVGIcon.ChevronRight}
                                color={ColorStyle("primary")}
                                size="small"
                                className={styles.TabNavigationNavLinkChevron}
                            />
                        </div>
                    </UI.Nav.Link>
                </UI.Nav.Item>
            );
        },
        [onTabNavigationClick, uiStore.isMobile, authStore.user?.role]
    );

    const handleLinkClick = useCallback(
        (url: string) => (e: React.MouseEvent) => {
            e.preventDefault();
            // Append cache-busting query parameter
            const cacheBustedUrl = new URL(url);
            cacheBustedUrl.searchParams.set("_t", Date.now().toString());

            window.open(cacheBustedUrl.toString(), "_blank", "noopener,noreferrer");

            setTimeout(() => {
                props.onDocumentOpened?.();
            }, 2000);
        },
        [props]
    );

    const renderListItem = useCallback(
        (item: file_item, screenSize: ScreenSize) => {
            const file = item;
            const listRows: React.ReactElement[][] = [];

            if (file) {
                const date = format(new Date(file.filedate), "dd.MM.yyyy");
                const displayFileName = getAssemblyDocumentDisplayName(file.name, props.assemblyDocumentTitles);

                // getUpdatedDisplayedDocuments already filters years at the source.
                if (
                    documentStore.selectedYears.length > 0 &&
                    !documentStore.selectedYears.includes(new Date(file.filedate).getFullYear().toString())
                ) {
                    return null;
                }

                let rows: React.ReactElement[] = [];

                const isFileViewed = authStore.user?.role === Role.MANAGER ? true : file.viewed;

                switch (screenSize) {
                    case ScreenSize.DESKTOP:
                        rows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell
                                    key={"c-1"}
                                    colspan={2}
                                    value={displayFileName}
                                    className={isFileViewed ? styles.FileName : styles.FileIsNotViewed}
                                />
                                <UI.List.Cell
                                    key={"c-3"}
                                    colspan={0.6}
                                    value={date}
                                    className={isFileViewed ? "" : styles.FileIsNotViewed}
                                />
                            </UI.List.Row>
                        ];
                        break;
                    case ScreenSize.MOBILE:
                        rows = [
                            <UI.List.Row key={"row-1"}>
                                <UI.List.Cell
                                    key={"c-1"}
                                    colspan={2}
                                    value={displayFileName}
                                    className={isFileViewed ? styles.FileName : styles.FileIsNotViewed}
                                />
                            </UI.List.Row>,
                            <UI.List.Row key={"row-2"}>
                                <UI.List.Cell
                                    key={"c-2"}
                                    colspan={1}
                                    value={date}
                                    className={isFileViewed ? "sublabel" : styles.FileIsNotViewedTest}
                                />
                            </UI.List.Row>
                        ];
                        break;
                }

                listRows.push(rows);
            }

            const sanitizedFileName = (file.name ?? "").replace(/["'`^=?.,%+ç$><()#&]/g, "");
            const encodedFileName = encodeURIComponent(sanitizedFileName);
            const fileExtension = file.extension ? "." + file.extension : "";

            const urlLink = `${NetworkConfig.openFileWithFilename}${file?.fileid}/${encodedFileName}${fileExtension}`;

            const icon = (
                <div className={`link d-flex ${styles.DocumentsListIcon}`} onClick={handleLinkClick(urlLink)}>
                    <UI.Icon icon={UI.SVGIcon.DocumentsNavBar} color={ColorStyle("primary")} />
                </div>
            );

            return (
                <UI.List.Item
                    key={`${file?.id}-${file?.category}`}
                    screenSize={screenSize}
                    rows={listRows}
                    icon={icon}
                />
            );
        },
        [documentStore.selectedYears, authStore.user?.role, handleLinkClick, props.assemblyDocumentTitles]
    );

    const renderTabNavigationTabPane = useCallback(() => {
        if (activeFileCategory) {
            return (
                <UI.Tab.Pane key={uuidv4()} eventKey={activeFileCategory.key}>
                    <UI.List.BasicList
                        screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                        items={documentStore.displayedDocuments}
                        renderItem={renderListItem}
                        itemsPerPage={uiStore.isMobile ? 5 : 10}
                        emptyListMessage={t("screens.realestate.documents.no_documents_found").toString()}
                    />
                </UI.Tab.Pane>
            );
        } else {
            return <></>;
        }
    }, [activeFileCategory, renderListItem, t, uiStore.isMobile, documentStore]);

    const renderTabNavigationItemsList = useCallback(() => {
        const listRows: JSX.Element[] = [];

        if (displayedFileCategories) {
            displayedFileCategories.forEach((displayedFileCategory) => {
                if (uiStore.isMobile) {
                    listRows.push(
                        tabNavigationItem(
                            displayedFileCategory.key,
                            displayedFileCategory.label,
                            displayedFileCategory.total
                        ),
                        activeFileCategory?.key === displayedFileCategory.key ? (
                            <UI.Col key={displayedFileCategory.key} className={`pl-0 pr-0 ${styles.columnTabContent}`}>
                                <UI.Tab.Content>{renderTabNavigationTabPane()}</UI.Tab.Content>
                            </UI.Col>
                        ) : (
                            <div key={displayedFileCategory.key}></div>
                        )
                    );
                } else {
                    listRows.push(
                        tabNavigationItem(
                            displayedFileCategory.key,
                            displayedFileCategory.label,
                            displayedFileCategory.total
                        )
                    );
                }
            });
        }
        return listRows;
    }, [
        displayedFileCategories,
        activeFileCategory?.key,
        tabNavigationItem,
        renderTabNavigationTabPane,
        uiStore.isMobile
    ]);

    const TabContainer = useCallback(() => {
        return (
            <UI.Tab.Container activeKey={activeFileCategory?.key} transition={false}>
                {uiStore.isMobile ? (
                    <UI.Row>
                        <UI.Col>
                            <UI.Nav variant="pills" className={`flex-column ${styles.TabNavigationNav}`}>
                                {renderTabNavigationItemsList()}
                            </UI.Nav>
                        </UI.Col>
                    </UI.Row>
                ) : (
                    <UI.Row>
                        <UI.Col
                            sm={5}
                            className={styles.TabNavigationNavCol}
                            style={window.innerWidth >= ScreenWidth.SM ? undefined : { borderRight: "none" }}
                        >
                            <UI.Nav variant="pills" className={`flex-column ${styles.TabNavigationNav}`}>
                                {renderTabNavigationItemsList()}
                            </UI.Nav>
                        </UI.Col>
                        <UI.Col sm={7}>
                            <UI.Tab.Content>{renderTabNavigationTabPane()}</UI.Tab.Content>
                        </UI.Col>
                    </UI.Row>
                )}
            </UI.Tab.Container>
        );
    }, [renderTabNavigationItemsList, renderTabNavigationTabPane, uiStore.isMobile, activeFileCategory?.key]);

    return (
        <div className={`${styles.TabNavigationContainer} my-2`}>
            <UI.Input
                type="search"
                onChange={onChangeQueryInput}
                placeholder={t("labels.search")}
                value={currentSearchQuery}
            ></UI.Input>
            <DocumentFilter allFileItems={initialFileItems ?? []} />
            <br />
            <TabContainer />
        </div>
    );
};

export const DocumentsList = Sentry.withProfiler(observer(DocumentsListBase));
